import axios from "axios";
import {useEffect, useState, useRef} from "react";
import {isAndroid, isIOS} from "react-device-detect";
import {useNavigate, useLocation} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import clientLogger from '../../../../utils/clientLogger';
import {checkOrderStatus, subscribeToOrderStatus, initializeSocket, startAggressivePolling, verifyPaymentStatus} from "../../../api/orderStatusSocket";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import {useSecondModal} from "../../../helpers/hooks/useSecondModal";
import "./bitpaymobile.css";
import {secondaryModals, secondOverlayState, selectedShippingAddress, giftCheckoutState, paymentStatusSnackbarState} from "../../../Recoil/Atoms";

const BASE_URL = process.env.REACT_APP_BASE_URL;


const BitPayMobileModal = ({data}) => {
  const modal = useSecondModal();
  const setModals = useSetRecoilState(secondaryModals);
  const [, setOverlay] = useRecoilState(secondOverlayState);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [timer, setTimer] = useState(600);
  const [isSecondsPassed, setIsSecondsPassed] = useState(false);
  const [isRefreshClicked, setIsRefreshClicked] = useState(false);
  const [url, setUrl] = useState(data?.strLink);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const [orderStatus, setOrderStatus] = useState(OrderStatus.NOT_PAID);
  const navigate = useNavigate();
  const location = useLocation();
  const pollingIntervalRef = useRef(null);
  const maxPollingTimeRef = useRef(null);
  // PAID / ERROR handled once: the initial check, the socket/poll listener,
  // the local poll and the success-page route can all report the same outcome.
  const doneRef = useRef(false);
  const setSnack = useSetRecoilState(paymentStatusSnackbarState);

  // GIFT-CARD orders (GC- prefixed id): success routes to /gift-success.
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const isGiftOrder = () => {
    const oid = localStorage.getItem("orderId");
    return !!giftCheckout || (oid && oid.startsWith("GC-"));
  };

  const stopLocalPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (maxPollingTimeRef.current) {
      clearTimeout(maxPollingTimeRef.current);
    }
  };

  // A failure is told by the global PaymentStatusSnackbar; "לנסות שוב"
  // reopens this modal's payment-method choice.
  const showFailure = () => setSnack({id: Date.now(), retry: "selectCardMobile"});

  // Payment confirmed: close the modal now (no overlay) and go to the success
  // page - its SuccessReveal is the confirmation (no snackbar on success).
  const navigateToSuccess = () => {
    const oid = localStorage.getItem("orderId");
    if (oid && !doneRef.current) {
      doneRef.current = true;
      clientLogger.debug("Bit Payment: Navigating to success page for order", oid);

      stopLocalPolling();

      if (isGiftOrder()) {
        localStorage.removeItem("orderId");
        navigate(`/gift-success/${oid}`);
      } else {
        navigate(`/payment-success/${oid}`);
        // Clean up after navigation has started
        setTimeout(() => localStorage.clear(), 100);
      }
      closeNow();
    }
  };

  // Redirect can also come from OUTSIDE the modal (the gift page's status poll
  // navigates on webhook activation): close at once. Without this the modal
  // used to stay open after the redirect.
  useEffect(() => {
    const p = location.pathname;
    if (!doneRef.current && (p.startsWith("/gift-success") || p.startsWith("/payment-success"))) {
      doneRef.current = true;
      stopLocalPolling();
      closeNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Bit reported ERROR: close now and show the failure snackbar.
  const handlePaymentError = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    clientLogger.debug("Bit Payment: Handling payment error");
    stopLocalPolling();
    closeNow();
    localStorage.removeItem("orderId");
    showFailure();
  };

  // Payment not completed in time: just close, no snackbar - the customer may
  // still pay from the SMS (the socket fallback then opens the success page).
  const handlePaymentNotCompleted = () => {
    clientLogger.debug("Bit Payment: Payment not completed, closing");
    stopLocalPolling();
    handleClose();
    localStorage.removeItem("orderId");
  };

  // Start polling for order status
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    clientLogger.debug("Bit Payment: Starting status polling");
    
    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const resp = await checkOrderStatus();
        if (resp && resp.data) {
          if (resp.data.orderStatus === OrderStatus.PAID) {
            clientLogger.debug("Bit Payment: Polling detected PAID status");
            setOrderStatus(OrderStatus.PAID);
            clearInterval(pollingIntervalRef.current);
            if (maxPollingTimeRef.current) {
              clearTimeout(maxPollingTimeRef.current);
            }
            navigateToSuccess();
          } else if (resp.data.orderStatus === OrderStatus.ERROR) {
            clientLogger.debug("Bit Payment: Polling detected ERROR status");
            setOrderStatus(OrderStatus.ERROR);
            clearInterval(pollingIntervalRef.current);
            if (maxPollingTimeRef.current) {
              clearTimeout(maxPollingTimeRef.current);
            }
            handlePaymentError();
          }
        }
      } catch (error) {
        console.error("Bit Payment: Error polling status:", error);
      }
    }, 2000);
    
    // Stop polling after 45 seconds and check final status
    maxPollingTimeRef.current = setTimeout(async () => {
      clearInterval(pollingIntervalRef.current);
      clientLogger.debug("Bit Payment: Max polling time reached, checking final status");
      
      try {
        const finalResp = await checkOrderStatus();
        if (finalResp && finalResp.data && finalResp.data.orderStatus === OrderStatus.PAID) {
          navigateToSuccess();
        } else {
          clientLogger.debug("Bit Payment: Payment not completed within timeout");
          handlePaymentNotCompleted();
        }
      } catch (error) {
        console.error("Bit Payment: Final status check failed:", error);
        handlePaymentNotCompleted();
      }
    }, 45000);
  };

  useEffect(() => {
    // Initialize socket connection
    initializeSocket();
    
    // Start aggressive polling immediately for Bit payments
    startAggressivePolling();
    
    // Do an initial check with enhanced verification. Gift orders skip it —
    // /user/checkorder doesn't apply to GC- ids; the gift-page poll + the
    // location-morph effect handle the redirect and close.
    if (localStorage.getItem("orderId") && !isGiftOrder()) {
      verifyPaymentStatus().then((resp) => {
        if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
          clientLogger.debug("Bit Payment: Initial verification shows PAID status");
          setOrderStatus(OrderStatus.PAID);
          navigateToSuccess();
        } else if (resp && resp.data && resp.data.orderStatus === OrderStatus.ERROR) {
          clientLogger.debug("Bit Payment: Initial verification shows ERROR status");
          setOrderStatus(OrderStatus.ERROR);
          handlePaymentError();
        } else {
          clientLogger.debug("Bit Payment: Waiting for payment completion...", resp?.data?.orderStatus);
        }
      }).catch((error) => {
        console.error("Bit Payment: Initial verification failed:", error);
      });
    }
    
    // Subscribe to status updates (primary method)
    const unsubscribe = subscribeToOrderStatus((resp) => {
      const orderId = localStorage.getItem("orderId");
      clientLogger.debug(`Bit Payment Status Update for Order ${orderId}`, {
        status: resp?.data?.orderStatus,
        timestamp: new Date().toISOString()
      });
      
      if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
        clientLogger.debug(`Bit Payment SUCCESS for Order ${orderId}`);
        setOrderStatus(OrderStatus.PAID);
        window.successfulPaymentReceived = true;
        sessionStorage.setItem('successfulPaymentReceived', 'true');
        
        // Clear polling since we got the socket event
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        if (maxPollingTimeRef.current) {
          clearTimeout(maxPollingTimeRef.current);
        }
        navigateToSuccess();
      } else if (resp && resp.data && resp.data.orderStatus === OrderStatus.ERROR) {
        clientLogger.debug(`Bit Payment ERROR for Order ${orderId}`);
        setOrderStatus(OrderStatus.ERROR);
        // Clear polling since we got the socket event
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        if (maxPollingTimeRef.current) {
          clearTimeout(maxPollingTimeRef.current);
        }
        handlePaymentError();
      } else {
        clientLogger.debug(`Bit Payment Status for Order ${orderId}: ${resp?.data?.orderStatus}`);
      }
    });
    
    // Clean up on unmount
    return () => {
      unsubscribe();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (maxPollingTimeRef.current) {
        clearTimeout(maxPollingTimeRef.current);
      }
    };
  }, []);

  // Same tab, NOT window.open(url, "_blank"): the new tab becomes the browser's
  // active tab, so coming back from Bit the customer lands on that helper tab
  // while this page sits frozen in a background tab and never sees PAID (prod
  // logs: no checkout tab ever polled again after a Bit webhook). The Bit link
  // is a universal/app link — the OS hands it to the Bit app and this page stays.
  const handleRedirectToPay = () => {
    window.location.href = url;
  };

  const closeNow = () => {
    setModals(state => ({
      ...state,
      bitPayMobile: {visible: false, data: {}},
      selectCardMobile: {visible: false},
    }));
    setOverlay(false);
  };

  // Fade-out close (timer expiry / not completed).
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(closeNow, 1000);
  };

  const handleBitPayment = async () => {
    if (isRefreshClicked) {
      return;
    }

    const checkResp = await checkOrderStatus();
    if (checkResp.data && checkResp.data.orderStatus === OrderStatus.PAID) {
      setOrderStatus(OrderStatus.PAID);
      return;
    }

    let gamaAuthResponse;
    const gamaAccessToken = localStorage.getItem("gamaAccessToken");
    const expiresAt = localStorage.getItem("gamaAccessTokenExpiresAt");

    try {
      if (!gamaAccessToken || expiresAt <= Date.now()) {
        gamaAuthResponse = await axios.post(`${BASE_URL}/gama-login`);

        if (gamaAuthResponse.data.status !== 200) {
          throw new Error("Gama auth failed.");
        }

        localStorage.setItem("gamaAccessToken", gamaAuthResponse.data.access_token);
        localStorage.setItem("gamaAccessTokenExpiresAt", (Date.now() + gamaAuthResponse.data.expires_in));
      }

      const gamaSessionResponse = await axios.post(`${BASE_URL}/gama-session`, {
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
      });

      if (gamaSessionResponse.data.status !== 200) {
        throw new Error("Gama session creating failed.");
      }

      const id = localStorage.getItem("orderId");

      const createTransactionResponse = await axios.post(`${BASE_URL}/gama-create-payment`, {
        orderId: id,
        amount: data.amountCharged,
        payerName: (isGiftOrder() && giftCheckout ? giftCheckout.buyerName : selectedAddress.fullName) || "Blends",
        payerPhoneNumber: isGiftOrder() && giftCheckout ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
        transactionId: gamaSessionResponse.data.transactionProcessingId,
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
        gamaSessionToken: gamaSessionResponse.data.token,
        transactionEndpoint: gamaSessionResponse.data.transactionProcessingEndpoint,
      });

      if (createTransactionResponse.data.status !== 200) {
        throw new Error("Gama transaction creating failed.");
      }

      if (isAndroid) {
        setUrl(createTransactionResponse.data.walletProviderApplicationSchemeAndroid);
        clientLogger.debug("Refresh Android");
      } else if (isIOS) {
        setUrl(createTransactionResponse.data.walletProviderApplicationSchemeIos);
        clientLogger.debug("Refresh iOS");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRefreshClick = async () => {
    if (isSecondsPassed) {
      setTimer(600);
      await handleBitPayment();
      setIsRefreshClicked(true);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(remainingSeconds).padStart(2, "0");
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer > 0) {
          return prevTimer - 1;
        } else {
          clearInterval(intervalId);
          // Final check before closing - this is the key fix
          clientLogger.debug('MOBILE TIMER EXPIRED - PERFORMING FINAL STATUS CHECK');
          performFinalStatusCheck();
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Function to perform final status check before modal closes
  const performFinalStatusCheck = async () => {
    try {
      clientLogger.debug('MOBILE FINAL STATUS CHECK: Checking order status before closing modal');
      const orderId = localStorage.getItem("orderId");

      if (!orderId || isGiftOrder()) {
        // Gift orders: /user/checkorder doesn't apply — the gift-page poll
        // redirects on activation; just close.
        clientLogger.debug('MOBILE FINAL STATUS CHECK: No orderId found, closing modal');
        handleClose();
        return;
      }

      const response = await checkOrderStatus();
      clientLogger.debug('MOBILE FINAL STATUS CHECK RESULT:', response?.data);

      if (response && response.data && response.data.orderStatus === OrderStatus.PAID) {
        clientLogger.debug('MOBILE FINAL STATUS CHECK: PAYMENT COMPLETED - NAVIGATING TO SUCCESS');
        setOrderStatus(OrderStatus.PAID);
        navigateToSuccess();
      } else {
        clientLogger.debug('MOBILE FINAL STATUS CHECK: PAYMENT NOT COMPLETED - CLOSING MODAL');
        handleClose();
      }
    } catch (error) {
      console.error('MOBILE FINAL STATUS CHECK ERROR:', error);
      // If there's an error checking status, still close the modal
      handleClose();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsSecondsPassed(true);
    }, 20000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const loadingTimer = setTimeout(() => setIsLoading(false), 2000);

    return () => clearTimeout(loadingTimer);
  }, []);

  if (isLoading) {
    return <div className="bitpay-spinner"></div>;
  }

  return (
<div className={`bitbody ${!isLoading ? "fadeInModal" : ""} ${isClosing ? "modal-closing" : ""}`}>
  <span className="bittimer">{formatTime(timer)}</span>
  <div className="bitcontent">
    <div className="bitinfo">
      <div className="bitamount">₪{data?.amountCharged?.toLocaleString('he-IL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
          })}
        <br />
        <p>בקשה לתשלום מבלנדס:
        < br />
        {isGiftOrder() ? "גיפט קארד בלנדס" : "תמונות ממוסגרות"}
        </p>
      </div>
    </div>
    <img className="bitface" src="/assets/images/bitfaceblends.svg" alt="" />
  </div>
  <p className="paragraph">
בחרתם לשלם באמצעות
<img src="/assets/images/bitnobgblends.svg" alt="bit blends" />
    <br />
ההזמנה תכנס ברגע שתשלימו את התשלום
    </p>
  <div className="buttons">
  <button className="button1" onClick={() => handleRedirectToPay()}>מעבר לאפליקציית ביט</button>
    <button className="button2" onClick={() => handleRefreshClick()}>שלח שוב</button>
  </div>
</div>
  );
};

export default BitPayMobileModal;
